import { twoFactorApi } from '@/api/twoFactor';
import { TWO_FACTOR_CONFIG } from '@/config/twoFactorConfig';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Code,
  Divider,
  Grid,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import QRCode from 'qrcode';
import React, { useCallback, useEffect, useState } from 'react';
import Totp from 'supertokens-web-js/recipe/totp';
import { TwoFactorSetupState } from './types';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onSkip?: () => void;
  allowSkip?: boolean;
}

export const TwoFactorSetupModal: React.FC<TwoFactorSetupModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  onSkip,
  allowSkip = false,
}) => {
  const toast = useToast();
  const [state, setState] = useState<TwoFactorSetupState>({
    step: 'qr',
    deviceName: null,
    secret: null,
    qrCodeString: null,
    recoveryCodes: [],
    error: null,
    isLoading: true,
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasDownloadedCodes, setHasDownloadedCodes] = useState(false);

  const initializeSetup = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await Totp.createDevice();

      if (response.status === 'OK') {
        // Generate QR code image from the otpauth URL
        const qrCodeDataUrl = await QRCode.toDataURL(response.qrCodeString);

        setState((prev) => ({
          ...prev,
          deviceName: response.deviceName,
          secret: response.secret,
          qrCodeString: qrCodeDataUrl,
          isLoading: false,
        }));
      } else if (response.status === 'DEVICE_ALREADY_EXISTS_ERROR') {
        setState((prev) => ({
          ...prev,
          error: 'A device is already set up. Please contact support if you need to reset.',
          isLoading: false,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: 'Failed to initialize 2FA setup. Please try again.',
          isLoading: false,
        }));
      }
    } catch (error) {
      console.error('Error creating TOTP device:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to initialize 2FA setup. Please try again.',
        isLoading: false,
      }));
    }
  }, []);

  const handleVerifyCode = async () => {
    if (!state.deviceName || verificationCode.length !== 6) {
      return;
    }

    setIsVerifying(true);
    setState((prev) => ({ ...prev, error: null }));

    try {
      const response = await Totp.verifyDevice({
        deviceName: state.deviceName,
        totp: verificationCode,
      });

      if (response.status === 'OK') {
        try {
          const recoveryResponse = await twoFactorApi.generateRecoveryCodes();
          setState((prev) => ({
            ...prev,
            step: 'recovery',
            recoveryCodes: recoveryResponse.codes,
          }));
          toast({
            title: '2FA Verified',
            description: 'Your authenticator app is now connected.',
            status: 'success',
            duration: 3000,
            position: 'top',
          });
        } catch (recoveryError) {
          console.error('Error generating recovery codes:', recoveryError);
          setState((prev) => ({
            ...prev,
            error: 'Failed to generate recovery codes. Please contact support.',
          }));
        }
      } else if (response.status === 'INVALID_TOTP_ERROR') {
        setState((prev) => ({
          ...prev,
          error: 'Invalid code. Please check your authenticator app and try again.',
        }));
      } else if (response.status === 'LIMIT_REACHED_ERROR') {
        setState((prev) => ({
          ...prev,
          error: 'Too many attempts. Please wait a few minutes and try again.',
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: 'Verification failed. Please try again.',
        }));
      }
    } catch (error) {
      console.error('Error verifying TOTP:', error);
      setState((prev) => ({
        ...prev,
        error: 'Verification failed. Please try again.',
      }));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDownloadCodes = () => {
    const codesText = state.recoveryCodes.join('\n');
    const blob = new Blob(
      [
        `${TWO_FACTOR_CONFIG.APP_NAME} Recovery Codes\n\nKeep these codes safe. Each code can only be used once.\n\n${codesText}`,
      ],
      {
        type: 'text/plain',
      },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${TWO_FACTOR_CONFIG.APP_NAME.toLowerCase()}-recovery-codes.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setHasDownloadedCodes(true);
  };

  const handleCopyCodes = () => {
    const codesText = state.recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast({
      title: 'Copied',
      description: 'Recovery codes copied to clipboard.',
      status: 'success',
      duration: 2000,
      position: 'top',
    });
    setHasDownloadedCodes(true);
  };

  const handleComplete = () => {
    setState((prev) => ({ ...prev, step: 'complete' }));
    onComplete();
    onClose();
  };

  const handleSkip = async () => {
    if (state.step === 'qr' && state.deviceName) {
      try {
        await Totp.removeDevice({ deviceName: state.deviceName });
      } catch (error) {
        console.error('Error removing unverified device:', error);
      }
    }

    if (onSkip) {
      onSkip();
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      if (state.step === 'qr' && state.deviceName) {
        Totp.removeDevice({ deviceName: state.deviceName })
          .then(() => {
            console.log('Cleaned up unverified TOTP device on modal close:', state.deviceName);
          })
          .catch((error) => {
            console.error('Error cleaning up unverified device:', error);
          });
      }

      setState({
        step: 'qr',
        deviceName: null,
        secret: null,
        qrCodeString: null,
        recoveryCodes: [],
        error: null,
        isLoading: true,
      });
      setVerificationCode('');
      setHasDownloadedCodes(false);
    }
  }, [isOpen, state.step, state.deviceName]);

  useEffect(() => {
    if (isOpen && state.step === 'qr' && !state.qrCodeString) {
      initializeSetup();
    }
  }, [isOpen, state.step, state.qrCodeString, initializeSetup]);

  const renderQRStep = () => (
    <VStack spacing={6} align="stretch">
      <Text>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</Text>

      {state.isLoading ? (
        <Center py={8}>
          <Spinner size="xl" />
        </Center>
      ) : state.qrCodeString ? (
        <>
          <Center>
            <Box bg="white" p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
              <Image src={state.qrCodeString} alt="2FA QR Code" boxSize="200px" />
            </Box>
          </Center>

          <Box>
            <Text fontSize="sm" color="gray.500" mb={2}>
              Can't scan? Enter this code manually:
            </Text>
            <Code p={3} display="block" whiteSpace="pre-wrap" wordBreak="break-all" fontSize="sm">
              {state.secret}
            </Code>
          </Box>
        </>
      ) : null}

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      <Divider />

      <Box>
        <Text fontWeight="medium" mb={2}>
          Enter the 6-digit code from your authenticator app:
        </Text>
        <HStack>
          <Input
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            textAlign="center"
            fontSize="xl"
            letterSpacing="0.5em"
            fontFamily="mono"
          />
          <Button
            variant="primary"
            onClick={handleVerifyCode}
            isLoading={isVerifying}
            isDisabled={verificationCode.length !== 6 || state.isLoading}
          >
            Verify
          </Button>
        </HStack>
      </Box>
    </VStack>
  );

  const renderRecoveryStep = () => (
    <VStack spacing={6} align="stretch">
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Save your recovery codes</Text>
          <Text fontSize="sm">
            These codes can be used to access your account if you lose your authenticator device.
            Each code can only be used once.
          </Text>
        </Box>
      </Alert>

      <Box bg="gray.800" p={4} borderRadius="md" border="1px solid" borderColor="gray.600">
        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
          {state.recoveryCodes.map((code, index) => (
            <Code
              key={index}
              p={2}
              bg="gray.700"
              color="white"
              borderRadius="sm"
              textAlign="center"
              fontFamily="mono"
            >
              {code}
            </Code>
          ))}
        </Grid>
      </Box>

      <HStack spacing={4}>
        <Button flex={1} onClick={handleDownloadCodes} variant="outline">
          Download Codes
        </Button>
        <Button flex={1} onClick={handleCopyCodes} variant="outline">
          Copy to Clipboard
        </Button>
      </HStack>

      {!hasDownloadedCodes && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Please download or copy your recovery codes before continuing.
        </Alert>
      )}
    </VStack>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={allowSkip ? handleSkip : () => {}}
      closeOnOverlayClick={allowSkip}
      closeOnEsc={allowSkip}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent px={3} py={3}>
        <ModalHeader>
          {state.step === 'qr' && 'Set Up Two-Factor Authentication'}
          {state.step === 'recovery' && 'Save Your Recovery Codes'}
        </ModalHeader>
        {allowSkip && <ModalCloseButton />}

        <ModalBody>
          {state.step === 'qr' && renderQRStep()}
          {state.step === 'recovery' && renderRecoveryStep()}
        </ModalBody>

        <ModalFooter>
          {allowSkip && state.step === 'qr' && (
            <Button variant="ghost" mr={3} onClick={handleSkip}>
              Skip for now
            </Button>
          )}
          {state.step === 'recovery' && (
            <Button
              variant="primary"
              onClick={handleComplete}
              isDisabled={!hasDownloadedCodes}
              fontWeight="bold"
            >
              I've saved my codes
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
